/*
 * Офлайн-пропуска кассы.
 *
 * Интернет в зале пропадает, смена — нет. Всё, что требует проверки
 * PIN (вход в кассу, одобрение старшего), обязано уметь работать без
 * сервера — иначе «упал роутер» превращается в «встал зал».
 *
 * Принцип: при каждой УСПЕШНОЙ онлайн-проверке касса оставляет себе
 * пропуск — не сам PIN, а его след (SHA-256), посоленный ключом
 * устройства. Подобрать код по следу нельзя, унести след на другую
 * кассу бессмысленно: соль другая. Без сети пропуск пускает ровно
 * тех, кто уже входил на этой кассе, с теми правами, что были при
 * последней живой проверке.
 */
import { policyOf } from '@dastarhan/core';
import type { PermissionMap } from '@dastarhan/contracts';

const KEY = 'dastarhan.pinCache';

export type OfflinePass = {
  token: string;
  user: { id: string; fullName: string };
  permissions: PermissionMap;
  discountLimitPct: number | null;
};

/** След PIN: необратимый, посоленный устройством. */
export async function pinPrint(pin: string, deviceKey: string): Promise<string> {
  const raw = new TextEncoder().encode(`${pin}·${deviceKey}`);
  const dig = await crypto.subtle.digest('SHA-256', raw);
  return [...new Uint8Array(dig)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function readAll(): Record<string, OfflinePass> {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '{}') as Record<string, OfflinePass>;
  } catch {
    return {};
  }
}

/** Выписать пропуск после живой проверки сервером. */
export function savePass(print: string, pass: OfflinePass): void {
  try {
    const all = readAll();
    all[print] = pass;
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    /* хранилище тесно — вход всё равно случился, просто без пропуска */
  }
}

/** Найти пропуск по следу PIN. */
export function readPass(print: string): OfflinePass | null {
  return readAll()[print] ?? null;
}

/**
 * Офлайн-одобрение старшего: НАСТОЯЩАЯ проверка, а не пропуск мимо.
 *
 * Если старший уже входил на этой кассе — его PIN узнаваем локально,
 * и права проверяются по последнему живому снимку. Возвращает имя
 * одобрившего, null-права («PIN узнан, но права не позволяют») или
 * null («PIN на этой кассе не знаком»).
 */
export function approverOffline(
  print: string,
  action: string,
): { name: string } | 'no-right' | null {
  const pass = readPass(print);
  if (!pass) return null;
  if (policyOf(pass.permissions, action) !== 'allowed') return 'no-right';
  return { name: pass.user.fullName };
}
